/**
 * useSphereType.ts
 *
 * Data hook for the Sphere Type Composer. Fetches a single merged schema
 * definition and walks its ancestor chain to build a breadcrumb, entirely
 * from cached, permission-honest reads — never derives provenance or access
 * client-side. Originally read-only (EAINT-892); EAINT-895 Phase 3 added the
 * working-draft overlay and `saveField` — see `readEditableSchema`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAnalytics } from '@center-technologies/analytics';
import { getActiveOrganizationId } from '@center-technologies/shared-api-clients';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { Schema } from 'types/schema';
import type { AspectDefinition, AspectRow } from 'types/aspect';
import type { GlyphColor } from '../types/glyph';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import {
  getSphereType,
  getSchemaGate,
  getSchemaNeeds,
  mapSchemaFetchError,
  type SchemaGate,
  type SchemaAspectNeeds,
} from '../api/schemaApi';
import { updateSphereType, type UpdateSphereTypeFields, type UpdateSphereTypeOutcome } from '../api/updateSphereType';
import { publishSphereType, type PublishSphereTypeOutcome } from '../api/publishSphereType';
import { listAspects } from '../api/marketplaceApi';
import { createAspect, slugifyAspectName } from '../api/aspectApi';
import type { NewAspectDraft } from '../components/molecules/studio/NewAspectDialogMolecule';

/**
 * Safety cap on ancestor-chain reads. Not a tunable — the spec's stated
 * purpose is a hard ceiling against a pathological/cyclical chain, not a
 * per-org configuration knob (Phase 1 build decision; see plan Open
 * Questions — no `useDynamicConfig` mechanism exists in the repo).
 */
export const LINEAGE_MAX_DEPTH = 8;

export type SphereTypeStatus = 'loading' | 'ready' | 'notFound' | 'degraded';

export interface LineageAncestor {
  schemaId: string;
  name: string;
  systemName: string;
  /** Localization key for `name`, so the breadcrumb translates like the rest of the document. */
  nameKey?: string;
  /**
   * The three facts Placement's parent card states about the family it names
   * (`type-composer.jsx:966-979`): what it is for, whether it is a family or
   * makes spheres itself, and the mark it is recognised by. All three come off
   * the ancestor read this walk already performs — the card had to render
   * without them only because the walk discarded them.
   */
  description?: string | null;
  isAbstract?: boolean;
  glyph?: string | null;
  glyphColor?: GlyphColor | null;
  /**
   * What THIS ancestor itself contributes — its own, not-further-inherited aspects/
   * perspectives/relationships/arcs (EAINT-933 Phase 1.2/4.1: Placement's manifest is a
   * walk of the whole ancestor chain, independent of what the type's own merged
   * `definition` shows). Each field is that ancestor's own array filtered to
   * `!is_inherited` — an ancestor's OWN declared items, not what it in turn inherited
   * from further up (that further-up ancestor is its own, separate entry in this same
   * `ancestors` list, so summing every entry's `own*` gives the whole chain's total with
   * no double-counting). Undefined when the fetched ancestor definition carried none.
   */
  ownAspects?: string[];
  ownPerspectives?: string[];
  ownRelationships?: string[];
  ownArcs?: string[];
}

export interface LineageResult {
  /** Viewable ancestors, nearest parent first. */
  ancestors: LineageAncestor[];
  /**
   * True when the chain did not resolve all the way to a root (an
   * unviewable/missing ancestor, or the depth cap). Deliberately carries no
   * reason code — a denied hop and a missing one must render identically.
   */
  truncated: boolean;
}

export interface UseSphereTypeResult {
  status: SphereTypeStatus;
  /**
   * The merged definition, or null unless `status === 'ready'`. When
   * `canEdit === true` this is the caller's working DRAFT (EAINT-895 B3),
   * not the published version — falling back to published only until the
   * first edit creates a draft. A locked (`canEdit !== true`) read is always
   * published; there is no draft to show.
   */
  definition: Schema | null;
  /** The ancestor breadcrumb, or null unless `status === 'ready'`. */
  lineage: LineageResult | null;
  refetch: () => void;
  /**
   * The `version_id` `saveField` sends as `If-Match` for the next write —
   * the draft's if one exists, else the published row's (the first save
   * creates the draft). Null while locked; there is nothing to save against.
   */
  editVersionId: string | null;
  /** True for the duration of an in-flight `saveField` call. */
  saving: boolean;
  /**
   * True after the most recent `saveField` resolved to a stale `If-Match`
   * (EAINT-895 B5). Never auto-clears by silently refetching — that would
   * clobber whatever the caller is still showing the user; it clears only on
   * the next successful save or an explicit `refetch()`.
   */
  conflict: boolean;
  /**
   * True after the most recent `saveField` rejected with anything other than
   * a stale `If-Match` (network failure, 5xx, etc.) — the states-matrix
   * `save-failed` state (spec § States per component). `updateSphereType`
   * deliberately rethrows any non-409 error "for the caller's generic error
   * handling"; this is that handling. Clears on the next successful save or
   * an explicit `refetch()`, same lifecycle as `conflict`.
   */
  saveError: boolean;
  /**
   * Patches one field group onto the working draft. Returns `'locked'`
   * without any network call when the schema isn't editable (spec
   * `lockedModeIssuesNoWrite`) or `saveField` overtook a state where the
   * hook does not yet know what to send as `If-Match`. Returns `'error'`
   * rather than throwing when the write itself fails for a reason other than
   * a stale `If-Match` — every call site treats "not `'saved'`" as
   * revert-worthy already, so this never needs its own branch downstream.
   */
  saveField: (fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
  /**
   * The header's explicit **Save draft** (EAINT-897 C11). An empty-fields
   * `saveField({})` call: because a `mode: 'draft'` write always resolves-or-
   * forks the accumulating draft version before applying whatever fields it
   * carries (`updateSchemaHelper.ts`'s `isDraftMode` branch), sending none
   * still creates the draft from published when absent and is otherwise a
   * true no-op on an existing one — idempotent, matching the spec's
   * `createDraftFromPublished` semantics without guessing at content to
   * resend. Autosave-on-blur already flushes a focused field's own edit
   * before this fires (the organism blurs the active element on click); this
   * function only has to guarantee the draft row itself exists.
   *
   * `pendingFields` is analytics-only: how many field edits the caller flushed on
   * its way here (`sphere_type_draft_saved`). It never changes what is written.
   */
  saveDraft: (pendingFields?: number) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
  /** True for the duration of an in-flight `saveDraft` call. */
  savingDraft: boolean;
  /**
   * The publish-readiness checklist (EAINT-895 B4), fetched only while
   * editable — a locked schema's composer never renders the gate bar. Null
   * while loading or for a locked/not-yet-ready schema.
   */
  gate: SchemaGate | null;
  gateStatus: 'idle' | 'loading' | 'ready' | 'error';
  /**
   * Per-aspect resolved needs (EAINT-897 C4, `GET /schemas/:id/needs`), keyed
   * by `AspectRef.systemName`. Fetched whenever a definition is loaded —
   * unlike `gate`, this is not restricted to `canEdit === true`: the
   * prototype's `r.needs` chips render identically in read-only mode.
   */
  needsByAspect: Record<string, SchemaAspectNeeds['needs']>;
  needsStatus: 'idle' | 'loading' | 'ready' | 'error';
  /**
   * Every aspect definition this workspace can resolve — caller org ∪ the platform tenant
   * (`GET /aspects/list`). Read here rather than in the composer for the same reason
   * `needsByAspect` is: the page owns the reads. `null` while it has not arrived, which
   * the composer treats as "unknown", never as "empty" — see its `aspectCatalog` prop.
   */
  aspectCatalog: AspectRow[] | null;
  /** True for the duration of an in-flight `publish()` call. */
  publishing: boolean;
  /**
   * Cuts the working draft over to published (EAINT-895 Phase 4). Resolves
   * `'locked'` without any network call under the same conditions `saveField`
   * does. On `'published'`, refetches — the draft read then 404s ("no draft
   * yet") and falls back to the freshly published row, which is exactly
   * "clears the draft" from the caller's point of view.
   */
  publish: () => Promise<PublishSphereTypeOutcome | 'locked'>;
  /**
   * `POST /aspects/`, the `blank`/`extend` routes of "Draft a new aspect" (EAINT-933 Phase
   * 3.4). Resolves the created aspect's `system_name` so the composer can attach and open it,
   * or rejects (e.g. `AspectSystemNameCollisionError`) for the dialog to show inline. The
   * `describe` route never reaches here — the composer forwards it to the live agent thread
   * instead, which proposes rather than writes.
   */
  draftAspect: (draft: NewAspectDraft) => Promise<string | undefined>;
}

interface CacheEntry {
  clientRef: ApiClient;
  /** Active organization the read was made under; a read is never served to another one. */
  organizationId: string | null;
  schemaId: string;
  draft: boolean;
  promise: Promise<Schema>;
}

/**
 * Bounds retained `Schema` payloads. A lineage walk reads at most
 * `LINEAGE_MAX_DEPTH` ancestors, so this holds a few chains without evicting
 * within a single walk. Matches `useOrgAdmin`'s cap (frontend standard Rule 8).
 */
const MAX_CACHE_ENTRIES = 8;

let cacheEntries: CacheEntry[] = [];

/**
 * Clear the module-level sphere-type read cache. Wired into
 * `useSchemaTypeCacheReset` — load-bearing on an identity switch, because
 * cached ancestors may be `view`-gated org schemas. Also used by tests.
 */
export function resetSphereTypeCache(): void {
  cacheEntries = [];
}

/**
 * Drop every cached read (draft AND published) for one schema so the next
 * read goes to the network. Used by `refetch` and by `saveField` after a
 * successful write — the two never need to be evicted independently, and a
 * write can move both a schema's draft state.
 */
function evictSphereType(client: ApiClient, schemaId: string): void {
  cacheEntries = cacheEntries.filter(
    (entry) => !(entry.clientRef === client && entry.schemaId === schemaId)
  );
}

/**
 * Cache successful reads only — a rejected promise is evicted so the next call
 * retries. Both the primary read and the lineage walk go through here, so
 * clicking a breadcrumb into an already-walked ancestor does not refetch it,
 * and one schema can never be represented twice with divergent data.
 *
 * `draft` is part of the cache key (EAINT-895 B3): a draft read and a
 * published read for the same schema never share an entry, so an edit that
 * only touches the draft can't be served stale published data or vice versa.
 */
function fetchSchemaCached(client: ApiClient, schemaId: string, draft = false): Promise<Schema> {
  const organizationId = getActiveOrganizationId();
  const hit = cacheEntries.find(
    (entry) =>
      entry.clientRef === client &&
      entry.organizationId === organizationId &&
      entry.schemaId === schemaId &&
      entry.draft === draft
  );
  if (hit) return hit.promise;

  // Drop entries from a superseded client before capping, so a stale client's
  // payloads are never what an eviction has to choose between.
  cacheEntries = cacheEntries.filter(
    (entry) => entry.clientRef === client && entry.organizationId === organizationId
  );
  if (cacheEntries.length >= MAX_CACHE_ENTRIES) {
    cacheEntries = cacheEntries.slice(cacheEntries.length - MAX_CACHE_ENTRIES + 1);
  }

  const promise = getSphereType(client, schemaId, { draft }).catch((err) => {
    cacheEntries = cacheEntries.filter(
      (entry) => !(entry.clientRef === client && entry.schemaId === schemaId && entry.draft === draft)
    );
    throw err;
  });
  cacheEntries.push({ clientRef: client, organizationId, schemaId, draft, promise });
  return promise;
}

function statusOf(err: unknown): number | undefined {
  const anyErr = err as { response?: { status?: number }; status?: number } | undefined;
  return anyErr?.response?.status ?? anyErr?.status;
}

/**
 * Resolves the definition the composer should render plus the `version_id`
 * the next `saveField` should send as `If-Match`. A locked schema (no edit
 * gate) is always published — there is no draft to layer over it. An
 * editable schema tries the working draft first; a 404 ("Schema draft not
 * found") means no draft exists yet, so the published row is both the
 * definition shown and the baseline the first save's `If-Match` targets.
 * Any other draft-read failure (403/503/etc.) rethrows — that is a real
 * outage, not "no draft yet", and must surface as `degraded`/`notFound`
 * exactly like a failed published read would.
 */
async function readEditableSchema(
  client: ApiClient,
  schemaId: string
): Promise<{ definition: Schema; editVersionId: string | null }> {
  const published = await fetchSchemaCached(client, schemaId, false);
  if (published.canEdit !== true) {
    return { definition: published, editVersionId: null };
  }
  try {
    const draft = await fetchSchemaCached(client, schemaId, true);
    return { definition: draft, editVersionId: draft.version_id };
  } catch (err) {
    if (statusOf(err) === 404) {
      return { definition: published, editVersionId: published.version_id };
    }
    throw err;
  }
}

/**
 * Walks the ancestor chain from `parentSchemaId` up to `LINEAGE_MAX_DEPTH` hops, one hop at
 * a time (no hook-per-ancestor). Stops on the FIRST failed hop — a denied/missing ancestor
 * truncates the chain there rather than skipping over it, since a viewable-but-out-of-order
 * chain would misrepresent the actual lineage.
 */
async function walkLineage(client: ApiClient, parentSchemaId: string | null): Promise<LineageResult> {
  const ancestors: LineageAncestor[] = [];
  let currentId = parentSchemaId;
  let hops = 0;

  while (currentId && hops < LINEAGE_MAX_DEPTH) {
    hops += 1;
    try {
      const ancestor = await fetchSchemaCached(client, currentId);
      // Undefined when empty, never `[]` — matches every other optional field here
      // (`description`, `glyph`, …), all absent-or-value, so a mock/fixture ancestor with
      // no own machinery still equality-compares clean against one that never set the key.
      const nonEmpty = <T>(items: T[]): T[] | undefined => (items.length ? items : undefined);
      ancestors.push({
        schemaId: ancestor.schema_id,
        name: ancestor.name,
        systemName: ancestor.system_name,
        nameKey: ancestor.localization_name_key,
        description: ancestor.description,
        isAbstract: ancestor.is_abstract,
        glyph: ancestor.glyph,
        glyphColor: ancestor.glyph_color,
        ownAspects: nonEmpty((ancestor.aspects ?? []).filter((a) => !a.is_inherited).map((a) => a.systemName)),
        ownPerspectives: nonEmpty(
          (ancestor.perspectives ?? []).filter((p) => !p.is_inherited).map((p) => p.label || p.name)
        ),
        ownRelationships: nonEmpty(
          (ancestor.relationships ?? []).filter((r) => !r.is_inherited).map((r) => r.label || r.name)
        ),
        ownArcs: nonEmpty((ancestor.defaultArcs ?? []).filter((a) => !a.is_inherited).map((a) => a.templateSystemName)),
      });
      currentId = ancestor.parent_schema_id;
    } catch {
      return { ancestors, truncated: true };
    }
  }

  return { ancestors, truncated: Boolean(currentId) };
}

/**
 * Fetches a sphere type and its ancestor breadcrumb for the read-only Sphere Type Editor.
 * `degraded` clears any previously loaded definition — a stale definition rendered next to
 * an authorization outage would misrepresent the type as readable.
 */
export function useSphereType(schemaId: string | undefined): UseSphereTypeResult {
  const apiClients = useApiClientsOrNull();
  const logAnalyticsEvent = useAnalytics();
  const [status, setStatus] = useState<SphereTypeStatus>('loading');
  const [definition, setDefinition] = useState<Schema | null>(null);
  const [lineage, setLineage] = useState<LineageResult | null>(null);
  const [editVersionId, setEditVersionId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [gate, setGate] = useState<SchemaGate | null>(null);
  const [gateStatus, setGateStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [needsByAspect, setNeedsByAspect] = useState<Record<string, SchemaAspectNeeds['needs']>>({});
  const [needsStatus, setNeedsStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [aspectCatalog, setAspectCatalog] = useState<AspectRow[] | null>(null);
  const [publishing, setPublishing] = useState(false);
  /**
   * Monotonic token identifying the newest in-flight read. A mounted-flag
   * cannot guard this: the cleanup clears it but the very next effect run sets
   * it again, so an older request resolving late would still commit. Walking a
   * breadcrumb (`schemaId` changes while a read is in flight) is exactly that
   * case, and would render the previous type's definition under the new type's
   * URL. Only the read whose token is still current may commit.
   */
  const requestIdRef = useRef(0);

  const fetchType = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const isCurrent = () => requestIdRef.current === requestId;

    // A missing id is genuinely unresolvable — there is no such type to show,
    // which is `notFound`, not an outage.
    if (!schemaId) {
      setDefinition(null);
      setLineage(null);
      setEditVersionId(null);
      setStatus('notFound');
      return;
    }

    // `useApiClientsOrNull` returns null until the session loads and again on
    // sign-out. That is a client that has not booted yet — neither an
    // authorization nor an availability failure — so it must stay `loading`
    // rather than render the outage panel. The effect re-runs when the client
    // arrives, because `apiClients` is a dependency.
    if (!apiClients?.schemaClient) {
      setDefinition(null);
      setLineage(null);
      setEditVersionId(null);
      setStatus('loading');
      return;
    }

    setStatus('loading');
    const client = apiClients.schemaClient;

    try {
      const { definition: schema, editVersionId: nextEditVersionId } = await readEditableSchema(client, schemaId);
      const lineageResult = await walkLineage(client, schema.parent_schema_id);

      if (!isCurrent()) return;
      setDefinition(schema);
      setLineage(lineageResult);
      setEditVersionId(nextEditVersionId);
      setConflict(false);
      setSaveError(false);
      setStatus('ready');
    } catch (err) {
      if (!isCurrent()) return;
      setDefinition(null);
      setLineage(null);
      setEditVersionId(null);
      setStatus(mapSchemaFetchError(statusOf(err)));
    }
  }, [schemaId, apiClients]);

  useEffect(() => {
    fetchType();
  }, [fetchType]);

  /**
   * Publish readiness (EAINT-895 B4) — fetched only while editable; a locked
   * schema's composer never renders the gate bar, so there's nothing to
   * check. Re-runs on `editVersionId` so the checklist reflects the current
   * draft after every save, not just the first read.
   */
  useEffect(() => {
    if (!apiClients?.schemaClient || !schemaId || !definition || definition.canEdit !== true) {
      setGate(null);
      setGateStatus('idle');
      return undefined;
    }
    let cancelled = false;
    setGateStatus('loading');
    getSchemaGate(apiClients.schemaClient, schemaId)
      .then((result) => {
        if (cancelled) return;
        setGate(result);
        setGateStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setGate(null);
        setGateStatus('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on canEdit + editVersionId, not the whole definition object
  }, [apiClients, schemaId, definition?.canEdit, editVersionId]);

  /**
   * Per-aspect needs (EAINT-897 C4) — unlike `gate`, fetched regardless of
   * `canEdit`: the prototype shows `r.needs` chips beside a composed aspect in
   * both read-only and editable renders. Re-runs on `editVersionId` for the
   * same reason the gate does — an answer given since the last read should
   * show up after the next save.
   */
  useEffect(() => {
    if (!apiClients?.schemaClient || !schemaId || !definition) {
      setNeedsByAspect({});
      setNeedsStatus('idle');
      return undefined;
    }
    let cancelled = false;
    setNeedsStatus('loading');
    getSchemaNeeds(apiClients.schemaClient, schemaId)
      .then((result) => {
        if (cancelled) return;
        const byAspect: Record<string, SchemaAspectNeeds['needs']> = {};
        result.aspects.forEach((entry) => {
          byAspect[entry.aspectSystemName] = entry.needs;
        });
        setNeedsByAspect(byAspect);
        setNeedsStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setNeedsByAspect({});
        setNeedsStatus('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on schemaId + editVersionId, not the whole definition object
  }, [apiClients, schemaId, definition?.schema_id, editVersionId]);

  /**
   * The workspace's resolvable aspect definitions (EAINT-897 Phase 2 slice 11). Keyed on the
   * CLIENT alone, not on the schema or its version: the registry is a property of the
   * workspace, so walking a breadcrumb between types must not refetch it, and a save cannot
   * change it. A failure leaves it `null` rather than `[]` — the composer's resolvability
   * readings must stay silent on a failed read, not declare every attachment undefined.
   */
  useEffect(() => {
    const client = apiClients?.schemaClient;
    // No open type means no composer to read a registry for — the same reason the primary
    // read itself issues nothing without a `schemaId`.
    if (!client || !schemaId) {
      setAspectCatalog(null);
      return undefined;
    }
    let cancelled = false;
    listAspects(client)
      .then((rows) => {
        if (!cancelled) setAspectCatalog(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setAspectCatalog(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClients, schemaId]);

  /**
   * Drops the cached read for this type before refetching, so `refetch` is a
   * real retry rather than a replay of the cached promise. The caller's own
   * explicit reload path out of a conflict (spec `staleEditSurfacesConflictNotClobber`) —
   * nothing in this hook refetches automatically on conflict.
   */
  const refetch = useCallback(() => {
    if (apiClients?.schemaClient && schemaId) {
      evictSphereType(apiClients.schemaClient, schemaId);
    }
    fetchType();
  }, [apiClients, schemaId, fetchType]);

  /**
   * Patches one field group onto the working draft (spec B2b: always
   * `mode: 'draft'`, never publishes). Fail-closed: no `apiClients`, no
   * `schemaId`, a locked schema, or a not-yet-resolved `editVersionId` all
   * return `'locked'` before any network call — `lockedModeIssuesNoWrite`.
   *
   * On success, silently re-resolves the draft (no `status` flip to
   * `'loading'`) so the next save's `If-Match` targets the version THIS
   * write just created — the route never echoes it back in its response.
   * On conflict, deliberately does NOT refetch: the caller's already-typed
   * value must survive a stale `If-Match`, not be clobbered by a reload the
   * hook decided to run on the caller's behalf.
   */
  const saveField = useCallback(
    async (fields: UpdateSphereTypeFields): Promise<UpdateSphereTypeOutcome | 'locked' | 'error'> => {
      if (!apiClients?.schemaClient || !schemaId || !definition || definition.canEdit !== true || editVersionId === null) {
        return 'locked';
      }
      const client = apiClients.schemaClient;
      const requestId = requestIdRef.current;
      const fieldNames = Object.keys(fields);

      setSaving(true);
      try {
        const outcome = await updateSphereType(
          client,
          definition.system_name,
          fields,
          editVersionId
        );

        if (outcome === 'conflict') {
          setConflict(true);
          return 'conflict';
        }

        // Spec § Analytics: `sphere_type_edited`, fired here rather than at each field
        // row because this is the only place a write is known to have LANDED — a row's
        // `onCommit` fires for a value the server may still refuse (`conflict`/`locked`).
        // `saveDraft`'s empty-fields call is not an edit and carries its own event.
        if (fieldNames.length > 0) {
          logAnalyticsEvent('sphere_type_edited', undefined, {
            schemaId,
            systemName: definition.system_name,
            fields: fieldNames.join(','),
            fieldCount: fieldNames.length,
          });
        }

        evictSphereType(client, schemaId);
        const { definition: fresh, editVersionId: nextEditVersionId } = await readEditableSchema(client, schemaId);
        // A newer read (navigation, refetch) superseded this save's refresh —
        // don't commit a definition for a type the hook has moved on from.
        if (requestIdRef.current !== requestId) return outcome;
        setDefinition(fresh);
        setEditVersionId(nextEditVersionId);
        setConflict(false);
        setSaveError(false);
        return outcome;
      } catch {
        // `updateSphereType` rethrows anything but a stale `If-Match` — this IS
        // that generic handling. Resolved, not rethrown: a fire-and-forget
        // `onCommit={() => onSaveField?.({...})}` call site (every field row)
        // would otherwise become an unhandled promise rejection.
        setSaveError(true);
        return 'error';
      } finally {
        setSaving(false);
      }
    },
    [apiClients, schemaId, definition, editVersionId, logAnalyticsEvent]
  );

  /**
   * Explicit **Save draft** (EAINT-897 C11) — see the interface doc. Tracked by
   * its own `savingDraft` flag rather than reusing `saveField`'s `saving`,
   * since the header button's label/disabled state is this action's alone,
   * not every per-field autosave's.
   */
  const saveDraft = useCallback(
    async (pendingFields = 0): Promise<UpdateSphereTypeOutcome | 'locked' | 'error'> => {
      setSavingDraft(true);
      try {
        const outcome = await saveField({});
        // Spec § Analytics: `sphere_type_draft_saved` — only on a draft that actually
        // exists afterwards. `pendingFields` is what the click flushed (the composer
        // blurs a mid-edit field before calling), which is the number this event exists
        // to answer: how much work an explicit Save draft was rescuing from autosave.
        if (outcome === 'saved') {
          logAnalyticsEvent('sphere_type_draft_saved', undefined, { schemaId, pendingFields });
        }
        return outcome;
      } finally {
        setSavingDraft(false);
      }
    },
    [saveField, logAnalyticsEvent, schemaId]
  );

  /**
   * Cuts the working draft over to published (EAINT-895 Phase 4). Fail-closed
   * to `'locked'` under the same conditions `saveField` uses — publish is
   * meaningless without a resolved schema to publish. On `'published'`,
   * refetches so the draft read 404s ("no draft yet") and falls back to the
   * freshly published row — the caller-visible "the draft is cleared".
   */
  const publish = useCallback(async (): Promise<PublishSphereTypeOutcome | 'locked'> => {
    if (!apiClients?.schemaClient || !schemaId || !definition || definition.canEdit !== true) {
      return 'locked';
    }
    const client = apiClients.schemaClient;
    setPublishing(true);
    try {
      const outcome = await publishSphereType(client, schemaId);
      if (outcome.outcome === 'published') {
        refetch();
      }
      return outcome;
    } finally {
      setPublishing(false);
    }
  }, [apiClients, schemaId, definition, refetch]);

  /**
   * `POST /aspects/` for the `blank`/`extend` routes — see the interface doc. Builds a minimal,
   * property-less `AspectDefinition` from the draft: `blank` has no `parent_aspect`, `extend`
   * sets it to `draft.extendsAspect` (the `system_name` the dialog's own catalog dropdown
   * picked — never free text). The `system_name` is slugged from `draft.name` client-side:
   * `createAspect.ts` takes the caller's `systemName` as-is, with no server-side namespacing
   * of its own (unlike `createSchema`/`createContext`).
   */
  const draftAspect = useCallback(
    async (draft: NewAspectDraft): Promise<string | undefined> => {
      if (!apiClients?.schemaClient || !definition) return undefined;
      const systemName = slugifyAspectName(draft.name);
      const name = { default: draft.name };
      const aspectDefinition: AspectDefinition = {
        system_name: systemName,
        name,
        properties: [],
        valuePolicy: { temporal: 'timeless', attribution: 'none', minProvenanceToProject: 'claimed', automation: 'manual' },
        ...(draft.route === 'extend' && draft.extendsAspect ? { parent_aspect: draft.extendsAspect } : {}),
      };
      const created = await createAspect(apiClients.schemaClient, {
        systemName,
        name,
        definition: aspectDefinition,
      });
      return created.systemName;
    },
    [apiClients, definition]
  );

  return {
    status,
    definition,
    lineage,
    refetch,
    editVersionId,
    saving,
    conflict,
    saveError,
    saveField,
    saveDraft,
    savingDraft,
    gate,
    gateStatus,
    needsByAspect,
    needsStatus,
    aspectCatalog,
    publishing,
    publish,
    draftAspect,
  };
}
